import { useState } from 'react';

import { Button, Field, Textarea } from '@/design-system';
import { INVOICE_MESSAGE_MAX, type TenantSettings } from '@/entities/tenant';
import { errorMessage } from '@/shared/api';

import { clientMessagesCopy as copy } from '../copy';
import { useUpdateSettings } from '../hooks';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';

export interface InvoiceMessageSettingProps {
    settings: TenantSettings;
}

/**
 * Het standaard eigen bericht in de mail die rapport en factuur samen brengt
 * (`invoiceWithReportMessage`).
 *
 * De verstuurdialoog begint ermee, en per verzending pas je het daar aan; een rapport
 * dat vanzelf met de factuur de deur uit gaat, neemt het zoals het hier staat. Een
 * tekstveld met een eigen knop, anders dan de schakelaars: elke toetsaanslag opslaan
 * zou de instelling halverwege een zin laten staan.
 */
export function InvoiceMessageSetting({ settings }: InvoiceMessageSettingProps) {
    const update = useUpdateSettings();
    const [draft, setDraft] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const value = draft ?? settings.invoiceWithReportMessage;
    const changed = draft !== null && draft !== settings.invoiceWithReportMessage;

    const save = () => {
        setSaved(false);
        update.mutate(
            { invoiceWithReportMessage: value },
            {
                onSuccess: () => {
                    setDraft(null);
                    setSaved(true);
                },
            },
        );
    };

    return (
        <div className="mt-[6px] rounded-[8px] bg-[var(--surface-subtle)] px-3 py-[10px]">
            <Field
                label={copy.invoiceMessage}
                description={`${copy.invoiceMessageHint} ${copy.characters(value.length, INVOICE_MESSAGE_MAX)}`}
            >
                <Textarea
                    value={value}
                    rows={3}
                    maxLength={INVOICE_MESSAGE_MAX}
                    placeholder={copy.invoiceMessagePlaceholder}
                    disabled={update.isPending}
                    onChange={(event) => {
                        setSaved(false);
                        setDraft(event.target.value);
                    }}
                />
            </Field>
            <div className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!changed || update.isPending}
                    onClick={save}
                >
                    {update.isPending ? copy.saving : copy.invoiceMessageSave}
                </Button>
                {saved ? (
                    <p role="status" className={`m-0 ${SMALL}`}>
                        {copy.invoiceMessageSaved}
                    </p>
                ) : null}
                {update.isError ? (
                    <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                        {errorMessage(update.error)}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
